/**
 * @Author: Zero
 * @Date: 2025-11-10 10:32:51
 * @Explain: 测试玩家管理模块
 */

import http from '../http';
import httpV2 from '../http-v2';
// type
import type {
  HighValueMessageResponse,
  TestReportSummaryResponse,
} from '@/pages/testerManage/utils/typings/aiSummary';
import type {
  ApiPromiseResult,
  FullRegionListResponse,
  GetAreaListData,
} from '@/pages/testerManage/utils/typings/list.d';
import type { HttpResponse } from '../data.d';
import type {
  PlatformGameInfoReqType,
  PlatformGameInfoResType,
  SourceEmailTagResType,
  GetViewSettingReqType,
  SetViewSettingReqType,
} from './data.d';

const MODEL = '/tester';

export default {
  // 测试人列表
  getTesterList: async (data: Record<any, any>) => {
    const testerDate = await http.post(`${MODEL}/list`, data, {
      params: { switchReqToUnderline: true },
    });

    // 1. 过滤出没有头像数据的项
    const itemsToFetch = testerDate.data.list.filter((item: any) => {
      return !item.steamGameAvatarUrl;
    });

    const uuidsToFetch = itemsToFetch.map((item: any) => item.uuid);

    let gameInfoList: any[] = [];
    // 2. 仅对缺失数据的项发起请求，全量已有时跳过
    if (uuidsToFetch.length > 0) {
      gameInfoList = await Promise.all(
        uuidsToFetch.map(async (uuid: string) => {
          const result = await http.post(
            `${MODEL}/game_info`,
            { uuid },
            { params: { switchReqToUnderline: true } },
          );
          return {
            ...result,
            data: { ...result.data, uuid },
          };
        }),
      );
    }

    // 3. 使用 Map 建立 uuid -> gameInfo 的映射
    const gameInfoMap = new Map(gameInfoList.map((g) => [g.data.uuid, g.data]));

    return {
      ...testerDate,
      data: {
        ...testerDate.data,
        list: testerDate.data.list.map((item: any) => {
          // 如果已有头像，直接返回原 item
          if (item.steamGameAvatarUrl) {
            return item;
          }
          // 否则使用请求回来的数据合并
          const fetchedInfo = gameInfoMap.get(item.uuid);
          return {
            ...item,
            // 统一字段名为驼峰，方便后续业务使用
            steamGameAvatarUrl: fetchedInfo?.steamGameAvatarUrl || '',
          };
        }),
      },
    };
  },

  // 测试管理列表
  getTesterUserList(data: Record<any, any>) {
    return http.post(`${MODEL}/user_list`, data, {
      params: { switchReqToUnderline: true },
    });
  },
  // 删除测试人
  onDelete(data: Record<any, any>) {
    return http.post(`${MODEL}/delete`, data, {
      params: { switchReqToUnderline: true },
    });
  },
  // 修改/保存-测试人配置信息
  editTesterInfo(data: Record<any, any>) {
    return http.post(`${MODEL}/save`, data);
  },
  // 获取 测试人管理页面-明细
  getTesterDetails(data: Record<any, any>) {
    return http.post(`${MODEL}/detail`, data, {
      params: { switchReqToUnderline: true },
    });
  },
  getViewSetting(data: GetViewSettingReqType) {
    return http.post(`${MODEL}/view_setting`, data, {
      params: { switchReqToUnderline: true },
    });
  },
  setViewSetting(data: SetViewSettingReqType) {
    return http.post(`${MODEL}/save_view_setting`, data, {
      params: { switchReqToUnderline: true },
    });
  },
  // 测试人管理页面-导入联系人
  sendTesterImportContact(data: Record<any, any>): ApiPromiseResult {
    return http.post(`${MODEL}/user_import`, data, {
      params: { switchReqToUnderline: true },
    });
  },
  // 邀请总数
  getTesterInvitationUsersTotal(data: Record<any, any>) {
    return http.post(`${MODEL}/user_invited_total`, data);
  },
  // 发送邀请请求
  sendTesterInvitationUsers(data: Record<any, any>) {
    return http.post(`${MODEL}/user_invite`, data);
  },
  // 发送删除请求
  deleteTesterInvitationList(data: Record<any, any>) {
    return http.post(`${MODEL}/user_delete`, data);
  },
  // 验证-测试人配置信息-title字段是否重复
  checkTitle(data: Record<any, any>) {
    return http.post(`${MODEL}/check_title`, data);
  },
  // 获取所有区域列表
  getFullRegionList(
    data: GetAreaListData = {},
  ): Promise<FullRegionListResponse> {
    return http.post(`/region_group/list`, data, {
      params: { switchReqToUnderline: true },
    });
  },
  // 获取测试邀请渠道报表-AI摘要
  getTesterReportSummary(
    data: Record<any, any>,
  ): Promise<TestReportSummaryResponse> {
    return httpV2.post(`${MODEL}/channel_report_summary`, data, {
      params: { switchReqToUnderline: true },
    });
  },
  // 获取测试邀请渠道报表-AI高价值分析
  getHighValueMessages(
    data: Record<any, any>,
  ): Promise<HighValueMessageResponse> {
    return httpV2.post(`${MODEL}/channel_report_highvalue_messages`, data, {
      params: { switchReqToUnderline: true },
    });
  },
  // 获取平台的游戏名称和头像
  getPlatformGameInfo(
    data: PlatformGameInfoReqType,
  ): Promise<HttpResponse<PlatformGameInfoResType>> {
    return http.post(`${MODEL}/game_info`, data, {
      params: { switchReqToUnderline: true },
    });
  },
  getSourceEmailTag(
    data: Record<any, any>,
  ): Promise<HttpResponse<SourceEmailTagResType>> {
    return httpV2.post(`${MODEL}/tag_email_stats`, data, {
      params: { switchReqToUnderline: true },
    });
  },
  // 获取 steam key 列表
  getSteamKeyList(data: Record<any, any>) {
    return http.post(`${MODEL}/steam_key_list`, data, {
      params: { switchReqToUnderline: true },
    });
  },
};
